/**
 * Component Purchase Options
 * 
 * Handles the functionality for the product purchase options
 * including one-time purchase and subscription options
 */

class PurchaseOptions extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.oneTimeOption = this.querySelector('[data-option="onetime"]');
    this.subscriptionOptions = this.querySelectorAll('[data-option="subscribe"]');
    this.oneTimePriceEl = this.querySelector('[data-onetime-price]');
    
    this.setupEventListeners();
    this.updatePrices();
  }

  setupEventListeners() {
    // Listen for variant changes from multiple sources
    
    // 1. Standard variant:change event
    document.addEventListener('variant:change', this.handleVariantChange.bind(this));
    
    // 2. Shopify PUB_SUB_EVENTS
    if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data && event.data.variant) {
          this.updatePricesForVariant(event.data.variant);
        }
      });
    }
    
    // 3. Direct approach - listen for changes on variant selectors
    const variantSelectors = document.querySelectorAll('.product-form__input select, .single-option-selector, input[name="id"]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', () => {
        setTimeout(() => {
          this.updateFromCurrentVariant();
        }, 100);
      });
    });
    
    // 4. Listen for option value changes (Dawn theme specific)
    if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.optionValueSelectionChange) {
      window.subscribe(window.PUB_SUB_EVENTS.optionValueSelectionChange, () => {
        setTimeout(() => {
          this.updateFromCurrentVariant();
        }, 100);
      });
    }
    
    // Listen for purchase option changes
    const purchaseOptions = this.querySelectorAll('.purchase-option__radio');
    purchaseOptions.forEach(option => {
      option.addEventListener('change', this.handlePurchaseOptionChange.bind(this));
    });
  }

  updateFromCurrentVariant() {
    const variantInput = document.querySelector('input[name="id"]');
    if (!variantInput || !variantInput.value) return;
    
    const variantId = variantInput.value;
    const productJson = this.getProductJson();
    
    if (!productJson) return;
    
    const selectedVariant = productJson.variants.find(v => v.id.toString() === variantId.toString());
    if (selectedVariant) {
      this.updatePricesForVariant(selectedVariant);
    }
  }
  
  getProductJson() {
    // Try multiple methods to get the product JSON
    
    // Method 1: From product-json script tag
    const productSection = document.querySelector(`product-info[data-section="${this.dataset.section}"]`) || 
                           document.getElementById(`MainProduct-${this.dataset.section}`);
    
    if (productSection) {
      const productJsonScript = productSection.querySelector('[data-product-json]') || 
                                document.getElementById(`ProductJson-${this.dataset.section}`);
      
      if (productJsonScript) {
        try {
          return JSON.parse(productJsonScript.textContent);
        } catch (e) {
          console.error('Error parsing product JSON from script tag:', e);
        }
      }
    }
    
    // Method 2: From window.product object (some themes use this)
    if (window.product) {
      return window.product;
    }
    
    // Method 3: From global productJson variable
    if (window.productJson) {
      return window.productJson;
    }
    
    console.warn('Could not find product JSON data');
    return null;
  }

  handleVariantChange(event) {
    if (!event.detail || !event.detail.variant) return;
    
    this.updatePricesForVariant(event.detail.variant);
  }
  
  updatePricesForVariant(variant) {
    if (!variant || !variant.price) return;
    
    console.log('Updating prices for variant:', variant);
    
    // Update one-time purchase price
    if (this.oneTimePriceEl) {
      this.oneTimePriceEl.innerHTML = this.formatMoney(variant.price);
    }
    
    // Update subscription prices
    this.subscriptionOptions.forEach(option => {
      const planId = option.dataset.plan;
      const priceEl = option.querySelector('[data-subscription-price]');
      const originalPriceEl = option.querySelector('[data-original-price]');
      const savingsPercentageEl = option.querySelector('[data-savings-percentage]');
      const savingsAmountEl = option.querySelector('[data-savings-amount]');
      const annualSavingsEl = option.querySelector('[data-annual-savings]');
      
      if (!priceEl || !originalPriceEl) return;
      
      // Set original price
      originalPriceEl.innerHTML = this.formatMoney(variant.price);
      
      // Get discount percentage from the savings badge
      let discountPercentage = 5; // Default 5%
      if (savingsPercentageEl) {
        const percentageText = savingsPercentageEl.textContent.match(/\d+/);
        if (percentageText) {
          discountPercentage = parseInt(percentageText[0], 10);
        }
      }
      
      // Calculate discounted price
      const discountMultiplier = (100 - discountPercentage) / 100;
      const discountedPrice = Math.round(variant.price * discountMultiplier);
      
      // Update subscription price
      priceEl.innerHTML = this.formatMoney(discountedPrice);
      
      // Calculate savings amount
      const savingsAmount = variant.price - discountedPrice;
      
      // Update savings amount text
      if (savingsAmountEl) {
        savingsAmountEl.innerHTML = `Save ${this.formatMoney(savingsAmount)} per order`;
      }
      
      // Calculate and update annual savings
      if (annualSavingsEl) {
        let annualOrders = 12; // Default to monthly (12 per year)
        
        // Determine frequency from the option title or description
        const optionTitle = option.querySelector('.purchase-option-card__title')?.textContent.toLowerCase() || '';
        
        if (optionTitle.includes('week')) {
          if (optionTitle.includes('2 week') || optionTitle.includes('two week')) {
            annualOrders = 26; // Bi-weekly
          } else if (optionTitle.includes('4 week') || optionTitle.includes('four week')) {
            annualOrders = 13; // Every 4 weeks
          } else {
            annualOrders = 52; // Weekly
          }
        } else if (optionTitle.includes('month')) {
          if (optionTitle.includes('2 month') || optionTitle.includes('two month')) {
            annualOrders = 6; // Bi-monthly
          } else if (optionTitle.includes('3 month') || optionTitle.includes('three month')) {
            annualOrders = 4; // Quarterly
          } else {
            annualOrders = 12; // Monthly
          }
        }
        
        const annualSavings = savingsAmount * annualOrders;
        annualSavingsEl.innerHTML = `That's ${this.formatMoney(annualSavings)} saved per year!`;
      }
      
      // Check if we have actual selling plan allocations
      if (variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
        const allocation = variant.selling_plan_allocations.find(a => a.selling_plan_id.toString() === planId);
        
        if (allocation) {
          // Use the actual discounted price from the allocation
          priceEl.innerHTML = this.formatMoney(allocation.price);
          
          // Recalculate savings
          const savingsAmount = variant.price - allocation.price;
          
          if (savingsAmountEl) {
            savingsAmountEl.innerHTML = `Save ${this.formatMoney(savingsAmount)} per order`;
          }
          
          if (annualSavingsEl) {
            const annualSavings = savingsAmount * annualOrders;
            annualSavingsEl.innerHTML = `That's ${this.formatMoney(annualSavings)} saved per year!`;
          }
        }
      }
    });
  }

  handlePurchaseOptionChange(event) {
    const selectedOption = event.target;
    const optionType = selectedOption.value;
    const planId = selectedOption.dataset.plan;
    
    console.log('Purchase option changed:', optionType, 'Plan ID:', planId);
    
    // Add selected class to the parent card
    this.querySelectorAll('.purchase-option-card').forEach(card => {
      card.classList.remove('purchase-option-card--selected');
    });
    
    selectedOption.closest('.purchase-option-card').classList.add('purchase-option-card--selected');
    
    // Update hidden input for cart form - try multiple approaches to find the input
    // 1. Look for the input within the product form
    let sellingPlanInput = document.querySelector('form[action*="/cart/add"] input[name="selling_plan"]');
    
    // 2. Look for the input with the form attribute
    if (!sellingPlanInput) {
      sellingPlanInput = document.querySelector('input[name="selling_plan"][form^="product-form"]');
    }
    
    // 3. Look for any selling_plan input
    if (!sellingPlanInput) {
      sellingPlanInput = document.querySelector('input[name="selling_plan"]');
    }
    
    if (sellingPlanInput) {
      if (optionType === 'subscribe' && planId) {
        sellingPlanInput.value = planId;
        console.log('Setting selling plan to:', planId);
      } else {
        sellingPlanInput.value = '';
        console.log('Clearing selling plan');
      }
    } else {
      console.error('Could not find selling_plan input, creating one');
      
      // If we can't find the input, create one and add it to the form
      const productForm = document.querySelector('form[action*="/cart/add"]');
      if (productForm) {
        sellingPlanInput = document.createElement('input');
        sellingPlanInput.type = 'hidden';
        sellingPlanInput.name = 'selling_plan';
        sellingPlanInput.value = optionType === 'subscribe' ? planId : '';
        productForm.appendChild(sellingPlanInput);
        console.log('Created selling_plan input with value:', sellingPlanInput.value);
      }
    }
    
    // Also dispatch a custom event that other scripts can listen for
    const customEvent = new CustomEvent('purchase-option:changed', {
      bubbles: true,
      detail: {
        optionType: optionType,
        planId: planId
      }
    });
    this.dispatchEvent(customEvent);
  }

  updatePrices(variant, productJson) {
    // If no variant is passed, use the current selected variant
    if (!variant) {
      const variantInput = document.querySelector('input[name="id"]');
      if (!variantInput || !variantInput.value) return;
      
      const variantId = variantInput.value;
      productJson = productJson || this.getProductJson();
      
      if (productJson && productJson.variants) {
        variant = productJson.variants.find(v => v.id.toString() === variantId.toString());
      }
    }
    
    if (!variant) return;
    
    this.updatePricesForVariant(variant);
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    
    // Fallback formatter if Shopify.formatMoney is not available
    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    });
    
    return formatter.format(cents / 100);
  }
}

customElements.define('purchase-options', PurchaseOptions);

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', function() {
  // Convert any existing purchase options to the custom element
  const purchaseOptionsContainers = document.querySelectorAll('.purchase-options:not(purchase-options)');
  purchaseOptionsContainers.forEach(container => {
    // Create a new purchase-options element
    const purchaseOptions = document.createElement('purchase-options');
    
    // Copy attributes
    Array.from(container.attributes).forEach(attr => {
      purchaseOptions.setAttribute(attr.name, attr.value);
    });
    
    // Move children
    while (container.firstChild) {
      purchaseOptions.appendChild(container.firstChild);
    }
    
    // Replace the container with the custom element
    container.parentNode.replaceChild(purchaseOptions, container);
  });
});
